import { HERD_CALL_AT, HERD_CALL_GAIN, HERD_SHUFFLE_GAIN, HERD_SHUFFLE_SLEW, HERD_VOICE_HEIGHT } from '@/audio/sounds/herd';
import type { BeatPartLoop } from '@/game/beatPartModule';
import type { CulvertQueueEvent, CulvertQueueSnapshot } from '@/story/act2/culvertQueue';
import { culvertRoutePointAt, hedgerowGroundY } from '@/world/hedgerow';

type VoiceEvent = Extract<CulvertQueueEvent, { kind: 'stall' | 'regain' }>;
type Point = { x: number; y: number; z: number };

/** Render-owned voice state for Beat 10's queue; fixed-step events are queued here. */
export class TwelveOutVoice {
  #queue: VoiceEvent[] = [];
  #calls: number[] = [];
  #clock: number | null = null;
  #bed: BeatPartLoop | null = null;
  #gain = 0;
  #peak = 0;
  #closed = false;

  constructor(private readonly audio: {
    playLoop(id: string, options: { gain: number; position: Point }): BeatPartLoop;
    playOneShot(id: string, options: { gain: number; position: Point; detune: number }): void;
  }) {}

  enqueue(event: VoiceEvent): void {
    this.#queue.push(event);
  }

  /** Stop only the loop: calls already scheduled are the last thing she hears. */
  close(): void {
    this.#closed = true;
    this.#stop(1.2);
  }

  reset(): void {
    this.#queue.length = 0;
    this.#calls.length = 0;
    this.#clock = null;
    this.#closed = false;
    this.#peak = 0;
    this.#stop(0.2);
  }

  dispose(): void {
    this.reset();
  }

  get debug(): { bedPlaying: boolean; bedGain: number; bedPeak: number; callsPending: number } {
    return {
      bedPlaying: this.#bed?.active === true,
      bedGain: this.#gain,
      bedPeak: this.#peak,
      callsPending: this.#calls.length,
    };
  }

  update(herd: CulvertQueueSnapshot): void {
    const clock = herd.clock;
    // Both the gain slew and call cadence use simulation time. `fastForward`
    // renders only a few frames across sixty fixed seconds, so render time would
    // produce a cadence neither the harness nor a player can hear consistently.
    const simDt = clock === null || this.#clock === null ? 0 : Math.max(0, clock - this.#clock);
    this.#clock = clock;

    // The head tells her where the animals stopped, rather than where their
    // huddle began: the panner says they are behind her without a HUD line.
    const point = culvertRoutePointAt(herd.headDistance);
    const at = {
      x: point.x,
      y: hedgerowGroundY(point.x, point.z) + HERD_VOICE_HEIGHT,
      z: point.z,
    };

    // Start only after muster. A bed beneath the waiting huddle would say they
    // were already moving before the player made that choice.
    if (clock !== null && !herd.ended && !this.#closed && this.#bed === null) {
      this.#bed = this.audio.playLoop('herd-shuffle', { gain: 0, position: at });
    }
    if (this.#bed !== null) {
      this.#bed.setPosition(at);
      const target = herd.drawn && !herd.ended && !this.#closed ? HERD_SHUFFLE_GAIN : 0;
      this.#gain += (target - this.#gain) * Math.min(1, simDt / HERD_SHUFFLE_SLEW);
      // A silent handle ramps too. The peak proves a live bed was actually
      // played, rather than reporting success for an unavailable audio context.
      if (this.#bed.active && this.#gain > this.#peak) this.#peak = this.#gain;
      this.#bed.setGain(this.#gain);
      if (herd.ended || this.#closed) this.#stop(1.2);
    }

    while (this.#queue.length > 0) {
      const event = this.#queue.shift();
      if (event === undefined) continue;
      // A regain cancels unsounded calls: continuing them would say she failed
      // precisely when the herd started walking again.
      if (event.kind === 'regain') this.#calls.length = 0;
      // Schedule from the fixed-step mark, not this render frame's clock. A
      // render hitch otherwise slides every call and can bunch the sequence.
      else for (const offset of HERD_CALL_AT) this.#calls.push(event.at + offset);
    }

    // The terminal scatters the string, so no call remains. Closing is different:
    // its already-scheduled calls continue under the fade after the bed releases.
    if (herd.ended) this.#calls.length = 0;
    while (clock !== null && (this.#calls[0] ?? Infinity) <= clock) {
      this.#calls.shift();
      this.audio.playOneShot('herd-stall', { gain: HERD_CALL_GAIN, position: at, detune: 45 });
    }
  }

  #stop(release: number): void {
    this.#bed?.stop(release);
    this.#bed = null;
    this.#gain = 0;
  }
}
